import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Bot,
  Check,
  Copy,
  Lightbulb,
  MessageSquareWarning,
  Pin,
  RefreshCw,
  Ruler,
  Sparkles,
  Target,
} from "lucide-react";
import { useIsAdmin } from "@/auth/useMe";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useSyntheticStances,
  useClearOverride,
  useOpinions,
  usePromoteOption,
  useResolveFeedback,
  useResolveGap,
  useSetOverride,
  useSynthesizeTopic,
  useTaxonomyFeedback,
  useTaxonomyGaps,
  useUnresolveFeedback,
  useUnresolveGap,
} from "@/lib/useOpinions";
import type { SyntheticStance, TaxonomyFeedback, TaxonomyGap, TeamPosition } from "@/lib/types";
import { cn } from "@/lib/utils";

// Friendly labels for the stance slugs the interview extracts.
const STANCE_LABELS: Record<string, string> = {
  keep: "Keep current",
  open_to_alternatives: "Open to alternatives",
  depends: "It depends",
  prefer_change: "Prefer change",
  unsure: "Unsure",
};

function StanceChips({ position }: { position: TeamPosition }) {
  const stances = position.distribution.stances ?? {};
  const entries = Object.entries(stances).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {entries.map(([stance, count]) => (
        <span
          key={stance}
          className="rounded-pill bg-bw-blue-muted/15 px-2.5 py-0.5 font-sans text-xs text-bw-navy"
        >
          {STANCE_LABELS[stance] ?? stance} ×{count}
        </span>
      ))}
      {typeof position.distribution.avg_conviction === "number" && (
        <span className="font-sans text-xs text-bw-ink-muted">
          conviction {position.distribution.avg_conviction}/5
        </span>
      )}
      <FamiliarityNote familiarity={position.distribution.familiarity} />
    </div>
  );
}

const FAMILIARITY_LABELS: Record<string, string> = {
  hands_on: "hands-on",
  familiar: "familiar",
  new_to_this: "new to this",
};

/** Who's behind the numbers: self-reported familiarity counts, so a reader can
 *  tell practitioner consensus from a poll of guesses. */
function FamiliarityNote({ familiarity }: { familiarity?: Record<string, number> }) {
  const entries = Object.entries(familiarity ?? {}).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return null;
  return (
    <span className="font-sans text-xs text-bw-ink-muted">
      · {entries.map(([level, count]) => `${count} ${FAMILIARITY_LABELS[level] ?? level}`).join(", ")}
    </span>
  );
}

/** Admin-only inline editor for pinning a curated position. */
/**
 * The checkable rules a position implies. Rendered as its own block rather than
 * folded into the decision prose, because that is the whole point: a reviewer or
 * a coding agent needs "approver_count = 2, on migration paths" as a rule it can
 * apply, not a sentence it has to parse back into one.
 */
function Gates({ position }: { position: TeamPosition }) {
  if (position.gates.length === 0) return null;
  return (
    <div className="rounded-card border border-bw-blue-muted/30 bg-bw-blue-muted/5 p-3">
      <p className="flex items-center gap-1.5 font-sans text-xs font-semibold text-bw-navy">
        <Ruler className="h-3.5 w-3.5" aria-hidden />
        Checkable rules
      </p>
      <ul className="mt-2 flex flex-col gap-1.5">
        {position.gates.map((gate) => (
          <li key={`${gate.name}-${gate.value}-${gate.scope ?? ""}`} className="font-sans text-xs text-bw-navy">
            <code className="rounded bg-white/70 px-1 py-0.5 font-mono text-[11px]">
              {gate.name} = {gate.value}
            </code>
            {gate.scope && <span className="text-bw-ink-muted"> · {gate.scope}</span>}
            {gate.basis && <span className="block text-bw-ink-muted">{gate.basis}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Where the position binds. Silent when nobody drew a boundary — an empty
 *  `applies_to` means team-wide, and saying so on every card would be noise. */
function Scope({ position }: { position: TeamPosition }) {
  const { applies_to: appliesTo, exceptions } = position.scope;
  if (appliesTo.length === 0 && exceptions.length === 0) return null;
  return (
    <p className="font-sans text-xs text-bw-ink-muted">
      {appliesTo.length > 0 && (
        <>
          <span className="font-semibold text-bw-navy">Applies to</span> {appliesTo.join("; ")}
        </>
      )}
      {appliesTo.length > 0 && exceptions.length > 0 && " · "}
      {exceptions.length > 0 && (
        <>
          <span className="font-semibold text-bw-navy">Except</span> {exceptions.join("; ")}
        </>
      )}
    </p>
  );
}

/**
 * Today, when the position asks for something else.
 *
 * The interview captures aspiration at full conviction on purpose — "we should
 * be testing this" is a real position. But a card that shows only the target
 * reads as a description of the codebase, and anything acting on it (a person
 * writing a PR, an agent writing code) will hold existing work to a bar nothing
 * here meets yet. So a diverging position says both, and labels which is which.
 */
function PracticeNote({ position }: { position: TeamPosition }) {
  if (position.practice_state !== "diverges") return null;
  return (
    <div className="rounded-card border border-bw-blue-muted/30 bg-white/60 p-3">
      <p className="flex items-center gap-1.5 font-sans text-xs font-semibold text-bw-navy">
        <Target className="h-3.5 w-3.5" aria-hidden />
        This is where we want to get to, not where we are
      </p>
      {position.current_practice && (
        <p className="mt-1 font-sans text-xs text-bw-ink-muted">Today: {position.current_practice}</p>
      )}
    </div>
  );
}

function OverrideEditor({ position, onDone }: { position: TeamPosition; onDone: () => void }) {
  const [decision, setDecision] = useState(position.decision ?? "");
  const [rationale, setRationale] = useState(position.rationale ?? "");
  const setOverride = useSetOverride();

  return (
    <form
      className="flex flex-col gap-2 rounded-card border border-bw-blue-muted/40 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!decision.trim()) return;
        setOverride.mutate(
          {
            topicKey: position.topic_key,
            decisionPointKey: position.decision_point_key,
            decision: decision.trim(),
            rationale: rationale.trim() || undefined,
          },
          { onSuccess: onDone },
        );
      }}
    >
      <label className="font-sans text-xs font-semibold text-bw-ink-muted" htmlFor="override-decision">
        Curated decision
      </label>
      <textarea
        id="override-decision"
        className="min-h-[72px] rounded-submenu border border-bw-blue-muted/40 p-2 font-sans text-sm"
        value={decision}
        onChange={(e) => setDecision(e.target.value)}
        maxLength={2000}
        disabled={setOverride.isPending}
      />
      <label className="font-sans text-xs font-semibold text-bw-ink-muted" htmlFor="override-rationale">
        Rationale (optional)
      </label>
      <textarea
        id="override-rationale"
        className="min-h-[56px] rounded-submenu border border-bw-blue-muted/40 p-2 font-sans text-sm"
        value={rationale}
        onChange={(e) => setRationale(e.target.value)}
        maxLength={4000}
        disabled={setOverride.isPending}
      />
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={!decision.trim() || setOverride.isPending}>
          {setOverride.isPending ? "Pinning…" : "Pin position"}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>
          Cancel
        </Button>
        {setOverride.isError && (
          <span className="font-sans text-xs text-bw-ink-muted">Couldn't save — try again.</span>
        )}
      </div>
    </form>
  );
}

/** Options interviewees named that nobody seeded. Admins can promote one into
 *  the taxonomy overlay — future interviews then present it with pros/cons
 *  like any seeded option (the growth loop from the M1 plan). */
function NamedOptions({ position }: { position: TeamPosition }) {
  const isAdmin = useIsAdmin();
  const promote = usePromoteOption();
  const [promoting, setPromoting] = useState<string | null>(null);
  const [pros, setPros] = useState("");
  const [cons, setCons] = useState("");
  const named = position.distribution.named_options ?? [];
  if (named.length === 0) return null;

  const split = (raw: string) => raw.split(";").map((s) => s.trim()).filter(Boolean);

  return (
    <div className="flex flex-col gap-2">
      <span className="font-sans text-xs text-bw-ink-muted">
        Also named in interviews:{" "}
        {named.map((name, i) => (
          <span key={name}>
            {i > 0 && ", "}
            <span className="font-semibold text-bw-navy">{name}</span>
            {isAdmin && (
              <button
                type="button"
                className="ml-1 text-bw-blue underline-offset-2 hover:underline"
                onClick={() => setPromoting(promoting === name ? null : name)}
              >
                promote
              </button>
            )}
          </span>
        ))}
      </span>
      {isAdmin && promoting && (
        <form
          className="flex flex-col gap-2 rounded-card border border-bw-blue-muted/40 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            promote.mutate(
              {
                decisionPointKey: position.decision_point_key,
                label: promoting,
                pros: split(pros),
                cons: split(cons),
                promotedFrom: promoting,
              },
              { onSuccess: () => setPromoting(null) },
            );
          }}
        >
          <span className="font-sans text-xs font-semibold text-bw-navy">
            Promote “{promoting}” into this decision point
          </span>
          <input
            value={pros}
            onChange={(e) => setPros(e.target.value)}
            aria-label="Pros, separated by semicolons"
            placeholder="Pros (separate with ;)"
            className="rounded-submenu border border-bw-blue-muted/40 px-2 py-1 font-sans text-xs"
          />
          <input
            value={cons}
            onChange={(e) => setCons(e.target.value)}
            aria-label="Cons, separated by semicolons"
            placeholder="Cons (separate with ;)"
            className="rounded-submenu border border-bw-blue-muted/40 px-2 py-1 font-sans text-xs"
          />
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" variant="secondary" disabled={promote.isPending}>
              {promote.isPending ? "Promoting…" : "Promote option"}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setPromoting(null)}>
              Cancel
            </Button>
            {promote.isError && (
              <span className="font-sans text-xs text-bw-ink-muted">Couldn't promote — try again.</span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

/** Where a persona's stance belongs: the same (topic, decision point) as a position. */
function slotKey(topicKey: string, decisionPointKey: string): string {
  return `${topicKey}::${decisionPointKey}`;
}

const BASIS_LABELS: Record<string, string> = {
  experience: "from experience",
  instructions: "from its instructions",
  prior: "from training",
  web_search: "from a web search",
};

/** One persona's stance: what, how firmly, where the knowledge came from, and why. */
function SyntheticStanceLine({ stance }: { stance: SyntheticStance }) {
  const subject = stance.option_label ?? stance.named_option ?? stance.option_key;
  const label = stance.stance ? (STANCE_LABELS[stance.stance] ?? stance.stance) : null;
  const runtime = [stance.voiced_by?.model, stance.voiced_by?.harness].filter(Boolean).join(" · ");
  return (
    <li className="flex flex-col gap-0.5">
      <span className="font-sans text-xs text-bw-navy">
        <span className={cn("font-semibold", stance.persona_retired && "line-through opacity-60")}>
          {stance.persona_name}
        </span>
        {label && <> · {label}</>}
        {subject && <> · {subject}</>}
        {stance.conviction != null && <> · conviction {stance.conviction}/5</>}
      </span>
      {stance.basis.length > 0 && (
        <span className="flex flex-wrap gap-1">
          {stance.basis.map((b) => (
            <span key={b} className="rounded-pill bg-bw-blue/10 px-2 py-0.5 font-sans text-[10px] text-bw-navy">
              {BASIS_LABELS[b] ?? b}
            </span>
          ))}
        </span>
      )}
      {stance.conditions && (
        <span className="font-sans text-xs text-bw-ink-muted">When: {stance.conditions}</span>
      )}
      {stance.rationale && <span className="font-sans text-xs text-bw-ink-muted">{stance.rationale}</span>}
      {stance.evidence.length > 0 && (
        <span className="font-sans text-xs text-bw-ink-muted">Evidence: {stance.evidence.join(", ")}</span>
      )}
      {(runtime || stance.accountable_upn) && (
        <span className="font-sans text-[11px] text-bw-ink-muted">
          {runtime && <>Ran on {runtime} (self-reported)</>}
          {runtime && stance.accountable_upn && " · "}
          {stance.accountable_upn && <>accountable: {stance.accountable_upn}</>}
        </span>
      )}
    </li>
  );
}

/** Persona stances on one decision point, set apart from the team's: synthesis
 *  never reads them, so they must never look like part of the position. */
function SyntheticStances({ stances }: { stances: SyntheticStance[] }) {
  if (stances.length === 0) return null;
  return (
    <div className="flex flex-col gap-2 rounded-card border border-dashed border-bw-blue-muted/60 p-3">
      <span className="flex items-center gap-1.5 font-sans text-[11px] font-semibold uppercase tracking-wide text-bw-ink-muted">
        <Bot className="h-3.5 w-3.5" aria-hidden />
        Synthetic voices · not counted in the position
      </span>
      <ul className="flex flex-col gap-3">
        {stances.map((s, i) => (
          <SyntheticStanceLine key={`${s.persona_key}-${s.option_key ?? s.named_option ?? i}`} stance={s} />
        ))}
      </ul>
    </div>
  );
}

/** Persona stances on decision points no person has a position on yet. */
function SyntheticOnlyStances({ stances }: { stances: SyntheticStance[] }) {
  const byDecisionPoint = useMemo(() => {
    const out = new Map<string, SyntheticStance[]>();
    for (const s of stances) {
      const key = slotKey(s.topic_key, s.decision_point_key);
      out.set(key, [...(out.get(key) ?? []), s]);
    }
    return out;
  }, [stances]);
  if (stances.length === 0) return null;
  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="font-sans text-sm font-semibold uppercase tracking-wide text-bw-ink-muted">
          Synthetic voices without a team position
        </h2>
        <p className="font-sans text-xs text-bw-ink-muted">
          Personas (AI agents, chiefly) answer the same questions, but their answers never vote. These are
          decision points only personas have answered so far.
        </p>
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        {[...byDecisionPoint.values()].map((group) => (
          <Card key={slotKey(group[0].topic_key, group[0].decision_point_key)}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{group[0].decision_point_title}</CardTitle>
              <p className="font-sans text-xs text-bw-ink-muted">{group[0].topic_title}</p>
            </CardHeader>
            <CardContent className="pt-0">
              <SyntheticStances stances={group} />
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

function PositionCard({ position, syntheticStances }: { position: TeamPosition; syntheticStances: SyntheticStance[] }) {
  const isAdmin = useIsAdmin();
  const [editing, setEditing] = useState(false);
  const clearOverride = useClearOverride();
  const curated = position.source === "curated";

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <CardTitle>{position.title}</CardTitle>
          <span
            className={cn(
              "flex shrink-0 items-center gap-1 rounded-pill px-3 py-1 font-sans text-xs font-semibold",
              curated ? "bg-bw-navy text-white" : "bg-bw-blue/10 text-bw-navy",
            )}
            title={
              curated
                ? `Pinned by ${position.author ?? "an admin"} — synthesis won't rewrite it`
                : `Synthesized from ${position.respondent_count} interview${position.respondent_count === 1 ? "" : "s"}`
            }
          >
            {curated ? <Pin className="h-3.5 w-3.5" aria-hidden /> : <Sparkles className="h-3.5 w-3.5" aria-hidden />}
            {curated ? "Curated" : "Aggregate"}
          </span>
        </div>
        <p className="font-sans text-xs text-bw-ink-muted">
          {position.decision_point_title}
          {position.context ? ` — ${position.context}` : ""}
        </p>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3 pt-0">
        {position.decision && <p className="font-sans text-sm text-bw-navy">{position.decision}</p>}
        {position.rationale && (
          <p className="font-sans text-sm text-bw-ink-muted">{position.rationale}</p>
        )}

        <PracticeNote position={position} />
        <Scope position={position} />
        <Gates position={position} />

        <StanceChips position={position} />
        <NamedOptions position={position} />
        <SyntheticStances stances={syntheticStances} />

        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-2 font-sans text-xs text-bw-ink-muted">
          <span>
            {position.respondent_count} respondent{position.respondent_count === 1 ? "" : "s"}
          </span>
          {/* Drift nudge: interviews have moved since this prose was set. */}
          {position.new_since_set > 0 && (
            <span className="rounded-pill bg-bw-blue-muted/20 px-2.5 py-0.5 font-semibold text-bw-navy">
              {position.new_since_set} new since set
            </span>
          )}
        </div>

        {isAdmin && !editing && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              {curated ? "Edit pin" : "Override"}
            </Button>
            {curated && (
              <Button
                variant="ghost"
                size="sm"
                disabled={clearOverride.isPending}
                onClick={() =>
                  clearOverride.mutate({
                    topicKey: position.topic_key,
                    decisionPointKey: position.decision_point_key,
                  })
                }
              >
                {clearOverride.isPending ? "Recomputing…" : "Remove pin"}
              </Button>
            )}
            {clearOverride.isError && (
              <span className="font-sans text-xs text-bw-ink-muted">
                Couldn't remove the pin — try again.
              </span>
            )}
          </div>
        )}
        {isAdmin && editing && <OverrideEditor position={position} onDone={() => setEditing(false)} />}
      </CardContent>
    </Card>
  );
}

function TopicSection({
  topicKey,
  positions,
  syntheticStances,
}: {
  topicKey: string;
  positions: TeamPosition[];
  syntheticStances: Map<string, SyntheticStance[]>;
}) {
  const isAdmin = useIsAdmin();
  const synthesize = useSynthesizeTopic();
  const first = positions[0];
  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-sans text-base font-semibold text-bw-navy">{first.topic_title}</h3>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {synthesize.isError && (
              <span className="font-sans text-xs text-bw-ink-muted">
                Recompute failed — try again shortly.
              </span>
            )}
            <Button
              variant="ghost"
              size="sm"
              disabled={synthesize.isPending}
              onClick={() => synthesize.mutate(topicKey)}
              title="Re-run the synthesis pass for this topic now"
            >
              <RefreshCw className={cn("h-4 w-4", synthesize.isPending && "animate-spin")} aria-hidden />
              {synthesize.isPending ? "Recomputing…" : "Recompute"}
            </Button>
          </div>
        )}
      </div>
      <div className="grid gap-6 sm:grid-cols-2">
        {positions.map((p) => (
          <PositionCard
            key={p.opinion_id}
            position={p}
            syntheticStances={syntheticStances.get(slotKey(p.topic_key, p.decision_point_key)) ?? []}
          />
        ))}
      </div>
    </section>
  );
}

const FEEDBACK_KIND_LABELS: Record<string, string> = {
  option_description: "Pros/cons wrong",
  option_label: "Named wrong",
  decision_point_framing: "Question misframed",
  other: "Catalog note",
};

/** What a row is about: topic · decision point · option, the kind pill, and
 *  the note itself — shared between the open queue and the resolved history. */
function FeedbackNote({ item }: { item: TaxonomyFeedback }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="font-sans text-xs text-bw-ink-muted">
        <span className="font-semibold text-bw-navy">{item.topic_title}</span> ·{" "}
        {item.decision_point_title}
        {/* Falls back to the raw slug when the option is gone from the
            catalog — acting on this feedback is what removes them. */}
        {(item.option_label || item.option_key) && (
          <> · {item.option_label || item.option_key}</>
        )}
        <span className="ml-1 rounded-pill bg-bw-blue-muted/20 px-2 py-0.5 text-[10px] font-semibold text-bw-navy">
          {FEEDBACK_KIND_LABELS[item.kind] ?? item.kind}
        </span>
      </span>
      <span className="font-sans text-sm text-bw-ink">“{item.note}”</span>
    </div>
  );
}

/**
 * Admin-only: what interviewees said is wrong with the *catalog* rather than
 * where they stand within it — "Sentry is more capable than your cons say",
 * "this question is a false choice". The counterpart to promoting a named
 * option: that grows the taxonomy, this corrects it. Acting on a row usually
 * means editing the in-code catalog, so the button here just closes it out.
 *
 * Resolving is a single un-confirmed click, so the resolved rows stay one
 * toggle away: the history section names who closed each row and when, and
 * Unresolve puts a hastily-closed one back in the queue.
 */
function CatalogFeedback() {
  const isAdmin = useIsAdmin();
  const { data, isError } = useTaxonomyFeedback(isAdmin);
  const resolve = useResolveFeedback();
  const unresolve = useUnresolveFeedback();
  const [showResolved, setShowResolved] = useState(false);
  // Hidden entirely for non-admins and when the queue is empty — an empty card
  // would be permanent furniture on the page. Resolved rows alone keep it
  // visible: they're the undo surface, and hiding them re-loses the history.
  if (!isAdmin || isError || !data || data.length === 0) return null;

  const open = data.filter((item) => !item.resolved_at);
  const resolved = data.filter((item) => item.resolved_at);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquareWarning className="h-4 w-4 text-bw-blue" aria-hidden />
          Catalog feedback ({open.length})
        </CardTitle>
        <p className="font-sans text-sm text-bw-ink-muted">
          Interviewees flagged these as wrong with the taxonomy itself. Fixing one
          is usually a catalog edit; resolve it here once that's done.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 pt-0">
        {open.map((item) => (
          <div
            key={item.feedback_id}
            className="flex items-start justify-between gap-3 border-b border-bw-blue-muted/30 pb-3 last:border-0 last:pb-0"
          >
            <FeedbackNote item={item} />
            <Button
              variant="ghost"
              size="sm"
              disabled={resolve.isPending}
              onClick={() => resolve.mutate(item.feedback_id)}
            >
              Resolve
            </Button>
          </div>
        ))}
        {open.length === 0 && (
          <span className="font-sans text-sm text-bw-ink-muted">
            Nothing open — everything flagged has been resolved.
          </span>
        )}
        {resolve.isError && (
          <span className="font-sans text-xs text-bw-ink-muted">Couldn't resolve — try again.</span>
        )}

        {resolved.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-bw-blue-muted/30 pt-3">
            <button
              type="button"
              className="self-start font-sans text-xs font-semibold text-bw-ink-muted hover:text-bw-navy"
              aria-expanded={showResolved}
              onClick={() => setShowResolved((v) => !v)}
            >
              {showResolved ? "▾" : "▸"} Resolved ({resolved.length})
            </button>
            {showResolved &&
              resolved.map((item) => (
                <div
                  key={item.feedback_id}
                  className="flex items-start justify-between gap-3 border-b border-bw-blue-muted/30 pb-3 opacity-70 last:border-0 last:pb-0"
                >
                  <div className="flex flex-col gap-1">
                    <FeedbackNote item={item} />
                    <span className="font-sans text-xs text-bw-ink-muted">
                      Resolved by {item.resolved_by ?? "an admin"}
                      {item.resolved_at ? ` · ${new Date(item.resolved_at).toLocaleString()}` : ""}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={unresolve.isPending}
                    onClick={() => unresolve.mutate(item.feedback_id)}
                  >
                    Unresolve
                  </Button>
                </div>
              ))}
            {unresolve.isError && (
              <span className="font-sans text-xs text-bw-ink-muted">
                Couldn't reopen — try again.
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * A self-contained prompt for an LLM coder to author the missing topic. Built
 * client-side from the gap row plus repo-stable authoring guidance — the SPA
 * ships in the same image as the backend, so the guidance can't drift from the
 * catalog it describes without a deploy touching both.
 */
function coderInstructions(gap: TaxonomyGap): string {
  return [
    "You are working in the North Star repo (bluwavelp/northstar). The team-position",
    "synthesizer flagged a gap the preference taxonomy cannot capture yet. Author it",
    "as a new topic (or a new decision point on an existing topic) so future",
    "interviews can collect stances on it.",
    "",
    `Gap: ${gap.question}`,
    gap.rationale ? `Why it matters: ${gap.rationale}` : null,
    `Surfaced from topic: ${gap.topic_title} (key: ${gap.topic_key})`,
    `Nearest existing decision point: ${gap.decision_point_title ?? gap.decision_point_key ?? "none"}`,
    `Gap key (for provenance in the PR description): ${gap.gap_key}`,
    "",
    "How the taxonomy works in this repo:",
    "- The catalog is versioned in code at backend/northstar/taxonomy/catalog.py:",
    "  groups -> topics -> decision points -> seeded options. Read the existing",
    "  topics in the same group first and match their authoring style exactly",
    "  (topic prose, decision-point prompts phrased as questions, option pros/cons",
    "  that are honest about trade-offs).",
    "- Slugs are a stable contract: NEVER rename or remove an existing topic,",
    "  decision-point, or option key. Adding topics/decision points/options does",
    "  not require a TAXONOMY_VERSION bump.",
    "- Make the new topic interviewable: include its key in PILOT_TOPIC_KEYS.",
    "- Seed each decision point with 2-5 options carrying real pros/cons. Only map",
    "  an option to the current stack when the system exists in NORTHSTAR_SYSTEMS.",
    "- Validate with tests/test_taxonomy.py, then run the full backend suite inside",
    "  the container per CLAUDE.md before opening a PR.",
    "",
    "Deliverable: a PR adding the topic / decision point(s) with seeded options and",
    "any test updates. Do not touch unrelated taxonomy entries.",
  ]
    .filter((line): line is string => line !== null)
    .join("\n");
}

/**
 * Admin-only: open questions the synthesis pass derived that no current
 * decision point can capture — missing topics, not unsettled positions. The
 * third taxonomy loop: promotion grows it, feedback corrects it, this extends
 * it. Acting on a gap is authoring the topic in code (a deploy, by design), so
 * each row carries copy-ready instructions for an LLM coder; Resolve closes it
 * out, and a closed gap stays closed when the next pass re-derives it.
 */
function CatalogGaps() {
  const isAdmin = useIsAdmin();
  const { data, isError } = useTaxonomyGaps(isAdmin);
  const resolve = useResolveGap();
  const unresolve = useUnresolveGap();
  const [showResolved, setShowResolved] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);
  const copiedTimer = useRef<number | null>(null);
  // A pending "Copied" reset must not fire into an unmounted card.
  useEffect(
    () => () => {
      if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
    },
    [],
  );
  // Hidden entirely for non-admins and when there's nothing — same reasoning
  // as the feedback card. Resolved rows alone keep it visible (undo surface).
  if (!isAdmin || isError || !data || data.length === 0) return null;

  const open = data.filter((g) => !g.resolved_at);
  const resolved = data.filter((g) => g.resolved_at);

  const copy = (gap: TaxonomyGap) => {
    // writeText rejects on an unfocused document or denied permission — a
    // silent unhandled rejection would leave the admin pasting nothing.
    void navigator.clipboard.writeText(coderInstructions(gap)).then(
      () => {
        setCopyFailed(false);
        setCopiedId(gap.gap_id);
        if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
        copiedTimer.current = window.setTimeout(() => setCopiedId(null), 2500);
      },
      () => setCopyFailed(true),
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Lightbulb className="h-4 w-4 text-bw-blue" aria-hidden />
          Catalog gaps ({open.length})
        </CardTitle>
        <p className="font-sans text-sm text-bw-ink-muted">
          Synthesis flagged these as questions the taxonomy can't capture yet.
          Copy the instructions into an LLM coder to author the topic; resolve
          the gap once it ships (it stays resolved on future recomputes).
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 pt-0">
        {open.map((gap) => (
          <div
            key={gap.gap_id}
            className="flex items-start justify-between gap-3 border-b border-bw-blue-muted/30 pb-3 last:border-0 last:pb-0"
          >
            <div className="flex flex-col gap-1">
              <span className="font-sans text-xs text-bw-ink-muted">
                <span className="font-semibold text-bw-navy">{gap.topic_title}</span>
                {(gap.decision_point_title || gap.decision_point_key) && (
                  <> · near {gap.decision_point_title || gap.decision_point_key}</>
                )}
              </span>
              <span className="font-sans text-sm text-bw-ink">{gap.question}</span>
              {gap.rationale && (
                <span className="font-sans text-xs text-bw-ink-muted">{gap.rationale}</span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => copy(gap)}>
                {copiedId === gap.gap_id ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-bw-blue" aria-hidden /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" aria-hidden /> Copy coder instructions
                  </>
                )}
              </Button>
              {/* aria-label disambiguates from the feedback card's Resolve —
                  the visible text stays short, the accessible name doesn't. */}
              <Button
                variant="ghost"
                size="sm"
                aria-label="Resolve gap"
                disabled={resolve.isPending}
                onClick={() => resolve.mutate(gap.gap_id)}
              >
                Resolve
              </Button>
            </div>
          </div>
        ))}
        {open.length === 0 && (
          <span className="font-sans text-sm text-bw-ink-muted">
            Nothing open — every flagged gap has been resolved.
          </span>
        )}
        {resolve.isError && (
          <span className="font-sans text-xs text-bw-ink-muted">Couldn't resolve — try again.</span>
        )}
        {copyFailed && (
          <span className="font-sans text-xs text-bw-ink-muted">
            Couldn't copy — select the text and copy manually.
          </span>
        )}

        {resolved.length > 0 && (
          <div className="flex flex-col gap-3 border-t border-bw-blue-muted/30 pt-3">
            <button
              type="button"
              className="self-start font-sans text-xs font-semibold text-bw-ink-muted hover:text-bw-navy"
              aria-expanded={showResolved}
              onClick={() => setShowResolved((v) => !v)}
            >
              {showResolved ? "▾" : "▸"} Resolved ({resolved.length})
            </button>
            {showResolved &&
              resolved.map((gap) => (
                <div
                  key={gap.gap_id}
                  className="flex items-start justify-between gap-3 border-b border-bw-blue-muted/30 pb-3 opacity-70 last:border-0 last:pb-0"
                >
                  <div className="flex flex-col gap-1">
                    <span className="font-sans text-xs text-bw-ink-muted">
                      <span className="font-semibold text-bw-navy">{gap.topic_title}</span>
                    </span>
                    <span className="font-sans text-sm text-bw-ink">{gap.question}</span>
                    <span className="font-sans text-xs text-bw-ink-muted">
                      Resolved by {gap.resolved_by ?? "an admin"}
                      {gap.resolved_at ? ` · ${new Date(gap.resolved_at).toLocaleString()}` : ""}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Unresolve gap"
                    disabled={unresolve.isPending}
                    onClick={() => unresolve.mutate(gap.gap_id)}
                  >
                    Unresolve
                  </Button>
                </div>
              ))}
            {unresolve.isError && (
              <span className="font-sans text-xs text-bw-ink-muted">
                Couldn't reopen — try again.
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Opinions — the materialized team positions (M1 Phase C). Peers see the
 * synthesized aggregate per decision point (with the anonymous quantitative
 * distribution); admins can pin a curated override, unpin it, or force a
 * recompute. Grouped by taxonomy group → topic, mirroring Preferences.
 */
export function OpinionsPage() {
  const { data, isLoading, isError, error } = useOpinions();
  // Best-effort: a failed read leaves the positions as they are.
  const { data: syntheticStances } = useSyntheticStances();

  // Persona stances by decision point, and the ones no position carries.
  const [syntheticBySlot, syntheticOnly] = useMemo(() => {
    const bySlot = new Map<string, SyntheticStance[]>();
    for (const s of syntheticStances ?? []) {
      const key = slotKey(s.topic_key, s.decision_point_key);
      bySlot.set(key, [...(bySlot.get(key) ?? []), s]);
    }
    const held = new Set((data ?? []).map((p) => slotKey(p.topic_key, p.decision_point_key)));
    const orphans = (syntheticStances ?? []).filter((s) => !held.has(slotKey(s.topic_key, s.decision_point_key)));
    return [bySlot, orphans] as const;
  }, [syntheticStances, data]);

  // Group by taxonomy group, then topic, preserving the API's stable order.
  const grouped = useMemo(() => {
    const byGroup = new Map<string, Map<string, TeamPosition[]>>();
    for (const pos of data ?? []) {
      const groupTitle = pos.group_title || "Other";
      const byTopic = byGroup.get(groupTitle) ?? new Map<string, TeamPosition[]>();
      const list = byTopic.get(pos.topic_key) ?? [];
      list.push(pos);
      byTopic.set(pos.topic_key, list);
      byGroup.set(groupTitle, byTopic);
    }
    return byGroup;
  }, [data]);

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <h1 className="text-4xl font-bold text-bw-navy">Opinions</h1>
        <p className="max-w-2xl font-sans text-base text-bw-ink-muted">
          The team positions we hold — synthesized from everyone's preference
          interviews (or pinned by an admin) per engineering decision point.
          This is what our coding agents will read over MCP.
        </p>
      </section>

      <CatalogFeedback />
      <CatalogGaps />

      {isLoading && <p className="font-sans text-sm text-bw-ink-muted">Loading positions…</p>}

      {isError && (
        <div className="flex items-center gap-2 rounded-card border border-bw-blue-muted/40 p-4 font-sans text-sm text-bw-ink-muted">
          <AlertCircle className="h-4 w-4 shrink-0 text-bw-blue" aria-hidden />
          {Number(error?.status) === 503
            ? "Opinions aren't configured in this environment yet."
            : "Couldn't load team positions. Try again shortly."}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-card border border-bw-blue-muted/40 p-6 font-sans text-sm text-bw-ink-muted">
          No team positions yet. They appear automatically once teammates finish
          preference interviews — the synthesis pass aggregates every take into
          a position per decision point.
        </div>
      )}

      {data && data.length > 0 && (
        <div className="flex flex-col gap-10">
          {[...grouped.entries()].map(([groupTitle, byTopic]) => (
            <section key={groupTitle} className="flex flex-col gap-6">
              <h2 className="font-sans text-sm font-semibold uppercase tracking-wide text-bw-ink-muted">
                {groupTitle}
              </h2>
              {[...byTopic.entries()].map(([topicKey, positions]) => (
                <TopicSection
                  key={topicKey}
                  topicKey={topicKey}
                  positions={positions}
                  syntheticStances={syntheticBySlot}
                />
              ))}
            </section>
          ))}
        </div>
      )}

      {data && <SyntheticOnlyStances stances={syntheticOnly} />}
    </div>
  );
}
